import { type AnalyticsContext, type AnalyticsEnvelope, buildEnvelope } from "./envelope";
import { isNounVerbedEventName } from "./naming";

/**
 * This module is imported from BOTH server code and "use client"
 * components (via the shared `clientTrack`) — it must stay free of
 * Node-only imports (the structured pino logger, `getAppVersion`'s
 * `node:child_process` lookup) or the client bundle fails to build.
 * Warnings use plain `console.warn`; app-version resolution is an
 * explicit required parameter so each environment supplies its own
 * Node-safe or browser-safe resolver.
 */

/**
 * The transport a tracked event is sent through. Client and server use
 * different concrete sinks (posthog-js vs posthog-node) but funnel
 * through the exact same envelope-building + naming-check code path, so
 * "the standard property envelope on every event" holds regardless of
 * which side emitted it.
 */
export interface AnalyticsSink {
  capture(eventName: string, envelope: AnalyticsEnvelope): void;
}

export interface TrackResult {
  ok: boolean;
  error?: string;
  envelope?: AnalyticsEnvelope;
}

export type Track = (eventName: string, props: Record<string, unknown>, ctx: AnalyticsContext) => TrackResult;

/** Registry-validation hook shape (see registry.ts). Kept structural so track.ts stays import-light. */
export type EventValidator = (eventName: string, props: Record<string, unknown>) => { ok: boolean; error?: string };

export interface TrackerOptions {
  /**
   * Optional schema-registry check run before emit. When supplied and it
   * fails, the event still emits but a loud warning is logged — analytics must
   * NEVER break the product flow that triggered it, so drift surfaces in CI
   * logs (and the dedicated registry test) rather than as a runtime throw.
   * Left unset by default so the base `createTracker` behavior is unchanged.
   */
  validate?: EventValidator;
}

/**
 * Builds a `track()` function bound to a sink + app-version resolver.
 * Exported as a factory (rather than one hardwired singleton) so tests
 * inject a fake sink and the real client/server modules inject their
 * respective posthog transports.
 */
export function createTracker(
  sink: AnalyticsSink,
  resolveAppVersion: () => string = () => "unknown",
  options: TrackerOptions = {},
): Track {
  return (eventName, props, ctx) => {
    if (!isNounVerbedEventName(eventName)) {
      const error = `Event name "${eventName}" is not noun_verbed snake_case`;
      console.warn(`[analytics] rejected event name "${eventName}": ${error}`);
      return { ok: false, error };
    }

    if (options.validate) {
      const verdict = options.validate(eventName, props);
      if (!verdict.ok) {
        // Loud, non-fatal: the taxonomy drifted but the flow must not break.
        console.warn(`[analytics] taxonomy drift for "${eventName}": ${verdict.error}`);
      }
    }

    const envelope = buildEnvelope(eventName, props, ctx, resolveAppVersion());
    try {
      sink.capture(eventName, envelope);
    } catch (err) {
      // Analytics must never break the product flow that triggered it.
      console.warn(`[analytics] sink capture failed for "${eventName}":`, err);
      return { ok: false, error: "sink capture failed", envelope };
    }
    return { ok: true, envelope };
  };
}
